import { type KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type HiveRuling,
  hiveCalls,
  type ThinkerBlendPreview,
  type ThinkerHivePolicy,
  type ThinkerTryResult,
  type ThinkerTryWeight,
} from "~/api/aiAdminTypes";
import type { ThinkerFacetInput } from "~/api/client";
import { useApi } from "~/api/provider";
import { MessageMarkdown } from "~/features/conversations/MessageMarkdown";
import { TextArea } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { HiveThought, type RulingDraft } from "./HiveThought";

const PROMPT_LIMIT = 2000;

/** "58" for a minute-long local answer, "1.2" for a quick one. */
const seconds = (ms: number) =>
  ms < 10_000 ? (ms / 1000).toFixed(1) : String(Math.round(ms / 1000));

const splitList = (text: string): string[] =>
  text
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

/** "Builder 60 · Tester 25 · Skeptic 15" — the top facets of a blend, in percentages. */
const blendLine = (weights: { name: string; weight: number }[], top = 3) =>
  weights
    .slice(0, top)
    .map((w) => `${w.name} ${Math.round(w.weight * 100)}`)
    .join(" · ");

type TryState =
  | { phase: "idle" }
  | { phase: "thinking" }
  | { phase: "blending" }
  | { phase: "answer"; result: ThinkerTryResult }
  | { phase: "blend"; blend: ThinkerBlendPreview }
  | { phase: "stopped" }
  | { phase: "error"; message: string };

/**
 * TRY IT — where a thinker is tested. One real completion as the thinker with its facet blend
 * prepended (exactly what a research seat gets), or, when the thinker's hive is on, THE THOUGHT:
 * every facet answers, they vote, the authority decides, and the impasses the hive could not
 * settle take YOUR CALL (a re-run with your rulings). "Blend only" is the dry run that shows how
 * the facets would blend for the question without spending a model call. Owner, 2026-10-10:
 * "where do I set up the thinkers and where do I test them?" — set up above, test here.
 */
export function TryIt({
  kind,
  id,
  name,
  facets,
  hive = null,
}: {
  kind: "local" | "persona";
  id: string;
  /** The thinker's display name, for "‹Name› is thinking…". */
  name: string;
  /** The facets as currently typed (saved or not), for the dry run. */
  facets: ThinkerFacetInput[];
  /** The stored hive policy, for the waiting line; null when unknown. */
  hive?: ThinkerHivePolicy | null;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [prompt, setPrompt] = useState("");
  const [interests, setInterests] = useState("");
  const [state, setState] = useState<TryState>({ phase: "idle" });
  const [rulings, setRulings] = useState<Record<string, RulingDraft>>({});
  const controller = useRef<AbortController | null>(null);
  const busy = state.phase === "thinking" || state.phase === "blending";
  const ready = prompt.trim().length > 0 && !busy;
  const asHive = hive?.enabled === true;

  async function send(withRulings: HiveRuling[] = []) {
    if (!prompt.trim() || busy) return;
    const own = new AbortController();
    controller.current = own;
    setState({ phase: "thinking" });
    const input = {
      prompt: prompt.trim(),
      interests: splitList(interests),
      ...(withRulings.length > 0 ? { hive: true, rulings: withRulings } : {}),
    };
    try {
      const result =
        kind === "local"
          ? await api.thinkers.tryLocal(id, input, own.signal)
          : await api.thinkers.tryPersona(id, input, own.signal);
      if (controller.current === own) {
        setState({ phase: "answer", result });
        if (withRulings.length === 0) setRulings({});
      }
    } catch (error) {
      if (controller.current !== own) return;
      if (own.signal.aborted) setState({ phase: "stopped" });
      else
        setState({
          phase: "error",
          message: error instanceof Error ? error.message : String(error),
        });
    }
  }
  async function blendOnly() {
    if (!ready || facets.length === 0) return;
    const own = new AbortController();
    controller.current = own;
    setState({ phase: "blending" });
    try {
      const blend = await api.thinkers.previewBlend({
        facets,
        situation: { intent: prompt.trim(), interests: splitList(interests) },
      });
      if (controller.current === own) setState({ phase: "blend", blend });
    } catch (error) {
      if (controller.current === own)
        setState({
          phase: "error",
          message: error instanceof Error ? error.message : String(error),
        });
    }
  }
  const stop = () => controller.current?.abort();
  const onKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };
  const rule = (impasseId: string, draft: RulingDraft | null) =>
    setRulings((prev) => {
      const next = { ...prev };
      if (draft) next[impasseId] = draft;
      else delete next[impasseId];
      return next;
    });
  const applyRulings = () =>
    void send(
      Object.entries(rulings).map(([impasseId, draft]) => ({
        impasseId,
        for: draft.for,
        ...(draft.note.trim() ? { note: draft.note.trim() } : {}),
      })),
    );
  const waiting =
    state.phase === "blending"
      ? t("Blending…")
      : asHive && hive
        ? t(
            "{{name}} is thinking as a hive… {{facets}} facets, about {{calls}} calls, a few minutes on a local model",
            { name, facets: facets.length, calls: hiveCalls(facets.length, hive) },
          )
        : kind === "local"
          ? t("{{name}} is thinking… this takes about a minute on a local model", { name })
          : t("{{name}} is thinking…", { name });

  return (
    <section
      className="flex flex-col gap-2 rounded border border-divider p-3"
      aria-label={t("Try it")}
    >
      <h4 className="font-medium">{t("Try it")}</h4>
      <Text variant="small">
        {asHive
          ? t(
              "Ask this thinker something: every facet answers, they vote, the authority decides — exactly what a research seat gets when the hive is on.",
            )
          : t(
              "Ask this thinker something: it answers once, as itself, with its facets blended for your question — exactly what a research seat gets.",
            )}
      </Text>
      <TextArea
        label={t("Ask this thinker something…")}
        rows={3}
        value={prompt}
        maxLength={PROMPT_LIMIT}
        disabled={busy}
        placeholder={t("e.g. Should we ship the new cache before the launch?")}
        onChange={(event) => setPrompt(event.target.value)}
        onKeyDown={onKey}
      />
      <Input
        label={t("Interests in play (optional)")}
        value={interests}
        fullWidth
        disabled={busy}
        placeholder={t("e.g. latency, cost")}
        onChange={(event) => setInterests(event.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        {busy ? (
          <>
            {/* Measured 2026-10-10: a local 4B model answers at ~4 tokens/s with the persona
                prompt in, so one call takes about a minute; a hive takes several. Say so. */}
            <p role="status" className="text-small text-text">
              {waiting}
            </p>
            <Button size="sm" variant="neutral" onClick={stop}>
              {t("Stop")}
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="primary" disabled={!ready} onClick={() => void send()}>
              {t("Send")}
            </Button>
            <Button
              size="sm"
              variant="neutral"
              disabled={!ready || facets.length === 0}
              onClick={() => void blendOnly()}
              title={facets.length === 0 ? t("Add a facet first to see a blend.") : undefined}
            >
              {t("Blend only")}
            </Button>
            <Text variant="small">{t("Enter sends · Shift+Enter for a new line")}</Text>
          </>
        )}
      </div>

      {state.phase === "error" ? (
        <p role="alert" className="text-small text-danger">
          {state.message}
        </p>
      ) : null}
      {state.phase === "stopped" ? (
        <Text variant="small">{t("Stopped before the thinker answered.")}</Text>
      ) : null}

      {state.phase === "blend" ? (
        state.blend.weights.length === 0 ? (
          <Text variant="small">{t("No facet would speak in this situation.")}</Text>
        ) : (
          <BlendView
            heading={t("Would speak as {{blend}}", { blend: blendLine(state.blend.weights) })}
            weights={state.blend.weights.map((w) => ({ ...w, semantic: null }))}
            prompt={state.blend.prompt}
            systemPrompt={null}
          />
        )
      ) : null}

      {state.phase === "answer" ? (
        <section className="flex flex-col gap-2" aria-label={t("The answer")}>
          {state.result.hive ? (
            <>
              {state.result.hive.impasses.some((i) => i.status === "ruled") ? (
                <Text variant="small">
                  {t("The owner ruled on {{count}} impasse(s); the thinker thought again.", {
                    count: state.result.hive.impasses.filter((i) => i.status === "ruled").length,
                  })}
                </Text>
              ) : null}
              <HiveThought
                name={name}
                thought={state.result.hive}
                rulings={rulings}
                onRule={rule}
                onApplyRulings={applyRulings}
              />
            </>
          ) : state.result.blend && state.result.blend.weights.length > 0 ? (
            <BlendView
              heading={t("Spoke as {{blend}} ({{method}})", {
                blend: blendLine(state.result.blend.weights),
                method: state.result.blend.method,
              })}
              weights={state.result.blend.weights}
              prompt={state.result.blend.prompt}
              systemPrompt={state.result.systemPrompt}
            />
          ) : (
            <Text variant="small">
              {t("No facets: the thinker answered from its plain system prompt.")}
            </Text>
          )}
          {state.result.hive ? null : (
            <div className="rounded border border-divider p-3 text-body">
              <MessageMarkdown content={state.result.text || "…"} />
            </div>
          )}
          <p className="text-small text-text-tertiary">
            {state.result.model} ·{" "}
            {t("answered in {{seconds}} s", { seconds: seconds(state.result.durationMs) })} ·{" "}
            {state.result.inputTokens} in / {state.result.outputTokens} out
          </p>
          {state.result.reasoning ? (
            <details className="text-small text-text-secondary">
              <summary>{t("Model-emitted reasoning")}</summary>
              <pre className="whitespace-pre-wrap break-words pt-2">{state.result.reasoning}</pre>
            </details>
          ) : null}
          {(!state.result.blend || state.result.blend.weights.length === 0) &&
          state.result.systemPrompt &&
          !state.result.hive ? (
            <details className="text-small text-text-tertiary">
              <summary>{t("What the thinker was told")}</summary>
              <p className="pt-2 font-medium">{t("System prompt")}</p>
              <pre className="whitespace-pre-wrap break-words">{state.result.systemPrompt}</pre>
            </details>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}

/** The blend as bars, then the fold with what the thinker was actually told. */
function BlendView({
  heading,
  weights,
  prompt,
  systemPrompt,
}: {
  heading: string;
  weights: ThinkerTryWeight[];
  prompt: string;
  systemPrompt: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-small font-medium text-text">{heading}</p>
      <ul className="flex flex-col gap-1.5" aria-label={t("Blend")}>
        {weights.map((weight) => {
          const percent = Math.round(weight.weight * 100);
          return (
            <li key={weight.id || weight.name} className="text-small text-text">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">
                  {percent}% {weight.name}
                </span>
                <span className="text-text-secondary">
                  {weight.shared.length > 0
                    ? t("shares: {{terms}}", { terms: weight.shared.join(", ") })
                    : t("shares nothing with the situation")}
                  {weight.semantic !== null
                    ? ` · ${t("semantic match {{value}}", { value: weight.semantic.toFixed(2) })}`
                    : ""}
                </span>
              </div>
              <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-bg-secondary">
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${percent}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
      <details className="text-small text-text-tertiary">
        <summary>{t("What the thinker was told")}</summary>
        <p className="pt-2 font-medium">{t("Blend prompt")}</p>
        <pre className="whitespace-pre-wrap break-words">{prompt}</pre>
        {systemPrompt ? (
          <>
            <p className="pt-2 font-medium">{t("System prompt")}</p>
            <pre className="whitespace-pre-wrap break-words">{systemPrompt}</pre>
          </>
        ) : null}
      </details>
    </div>
  );
}
